// @flow weak

import _ from 'lodash';

import { RouterSelectors } from '@zapier/toolbox-redux-framework';
import { AUTH_FAILED_CONN, SHARED_AUTH_PARAM } from '@zapier/auth-connect';

import { Account } from '@zapier/shared-entities';
import * as Auth from 'app/entities/Auth';
import ActionTypes from 'app/auth/ActionTypes';
import DashboardActions from 'app/dashboard/DashboardActions';
import Data from 'app/auth/Data';
import ServiceUtils from 'app/service/ServiceUtils';
import preload, { preloadItems } from 'app/common/preload';
import { NEW_AUTH_FORM } from 'app/auth/utils/constants';
import {
  getAuths,
  hasLoadedAuths,
  isAuthShared,
  isLoadingAuths,
} from 'app/auth/Selectors';

import type { AuthType } from 'app/auth/types';
import type { ID } from 'app/common/types';

const updateAuths = ops => ({
  type: ActionTypes.UPDATE_AUTHS,
  ops,
});

const loadAuths = () => ({
  type: ActionTypes.LOAD_AUTHS,
});

const prepNewAuthData = (params, service, state) => {
  const accountId = Account.selectors.all.currentAccountId(state);
  const isOauth = ServiceUtils.isOauth(service);
  const hasUpfrontFields = ServiceUtils.hasUpfrontFields(service);
  return { ...params, accountId, hasUpfrontFields, isOauth };
};

// If the error is an `AUTH_FAILED_CONN` error then the user closed
// the popup. No need to inform them that the auth didn't connect
// since they initiated the flow.
const ignoreUserInitiatedConnectionErrors = error => {
  if (error && error.code !== AUTH_FAILED_CONN) {
    throw error;
  }
};

const AuthActions = {
  updateAuths,

  loadAuths(params) {
    return (dispatch, getState) => {
      if (isLoadingAuths(getState())) {
        return Promise.resolve();
      }

      dispatch(loadAuths());
      const currentAccountId = Account.selectors.all.currentAccountId(
        getState()
      );

      return Data.auths(currentAccountId, params).then(auths => {
        dispatch(
          updateAuths([
            {
              type: 'reset',
              data: auths || [],
            },
          ])
        );
      });
    };
  },

  loadAuthsIfNotLoaded(params) {
    return (dispatch, getState) => {
      if (!hasLoadedAuths(getState())) {
        dispatch(AuthActions.loadAuths(params));
      }
    };
  },

  connectAuth(
    params,
    {
      loadAuthsParams,
      onDangerouslyReceiveSuccess = () => {},
    }: {
      loadAuthsParams: {
        shouldIncludeDestinationAuths: boolean,
      },
      onDangerouslyReceiveSuccess: (
        id?: string,
        auths: { [key: ID]: AuthType }
      ) => void,
    } = {}
  ) {
    return (dispatch, getState) => {
      params.serviceKey = params.service && params.service.id;
      params.destinationServiceKey =
        params.destinationService && params.destinationService.id;

      if (!params.serviceKey) {
        throw new Error('You must provide an API to connect.');
      }
      const addAuthToStore = data => {
        dispatch(updateAuths([{ data, type: 'add' }]));
        return data;
      };
      return dispatch({
        type: ActionTypes.CONNECT_AUTH,
        promise: Data.connectAuth(
          prepNewAuthData(params, params.service, getState())
        )
          .then(authId => {
            return authId
              ? Data.auth(authId)
              : dispatch(AuthActions.loadAuths(loadAuthsParams));
          })
          .then(data => {
            return data && data.id ? addAuthToStore(data) : null;
          })
          .then(data => {
            if (data && data.id) {
              const auths = getAuths(getState());
              onDangerouslyReceiveSuccess(data.id, auths);
            }
            return data;
          })
          .catch(ignoreUserInitiatedConnectionErrors),
      });
    };
  },

  reconnectAuth(
    oldAuthId,
    params,
    onDangerouslyReceiveSuccess: (id?: string) => void = () => {}
  ) {
    return (dispatch, getState) => {
      params.serviceKey = params.service && params.service.id;
      params.destinationServiceKey =
        params.destinationService && params.destinationService.id;

      if (!oldAuthId) {
        throw new Error('You must provide an existing auth id.');
      }
      if (!params.serviceKey) {
        throw new Error('You must provide an API to reconnect.');
      }
      const preppedParams = {
        ...prepNewAuthData(params, params.service, getState()),
        isShared: isAuthShared(oldAuthId, getState()),
      };
      return dispatch({
        type: ActionTypes.RECONNECT_AUTH,
        id: oldAuthId,
        promise: Data.reconnectAuth(preppedParams, oldAuthId)
          .then(Data.auth)
          .then(result => {
            onDangerouslyReceiveSuccess(result.id);
            dispatch(DashboardActions.loadHeldReasons());
            return result;
          })
          .catch(ignoreUserInitiatedConnectionErrors),
      });
    };
  },

  reconnectAuthWithUsage(...args) {
    return dispatch => {
      return dispatch(AuthActions.reconnectAuth(...args)).then(() => {
        // Reload auth counts since the existing auth counts
        // won't have the newly reconnected auth ID in them
        dispatch(AuthActions.loadAuthCounts());
      });
    };
  },

  updateAuth(id, newData) {
    return {
      type: ActionTypes.UPDATE_AUTH,
      id,
      newData,
    };
  },

  saveAuth(id) {
    return (dispatch, getState) => {
      const authData = getState().auth.auths[id];
      return dispatch({
        type: ActionTypes.SAVE_AUTH,
        id,
        authData,
        promise: Data.updateAuth(authData),
      });
    };
  },

  updateAuthState(id, newState) {
    return {
      type: ActionTypes.UPDATE_AUTH_STATE,
      id,
      newState,
    };
  },

  resetAuthStates() {
    return {
      type: ActionTypes.RESET_AUTH_STATES,
    };
  },

  testAuth(id) {
    return dispatch => {
      dispatch({ type: ActionTypes.TEST_AUTH, id });
      Data.testAuth(id).then(
        () => dispatch({ type: ActionTypes.TEST_AUTH_DONE, id }),
        err => dispatch({ type: ActionTypes.TEST_AUTH_FAIL, err, id })
      );
    };
  },

  loadAuthCounts() {
    return {
      type: ActionTypes.LOAD_AUTH_COUNTS,
      promise: Data.loadAuthCounts(),
    };
  },

  deleteAuth(id) {
    return {
      id,
      type: ActionTypes.DELETE_AUTH,
      promise: Data.deleteAuth(id),
    };
  },

  initializeCollections() {
    const id = NEW_AUTH_FORM;
    return (dispatch, getState) => {
      return dispatch(
        Auth.actions.createCollectionUiEntity(
          {
            id,
            account_id: Account.selectors.all.currentAccountId(getState()),
            is_shared: !!RouterSelectors.getQueryParam(
              getState(),
              SHARED_AUTH_PARAM
            ),
          },
          { isNewAuth: true }
        )
      );
    };
  },

  setDefaults(defaultFields) {
    return (dispatch, getState) => {
      const id = Auth.selectors.id(Auth.selectors.all.authEntity(getState()));
      if (_.isUndefined(Auth.selectors.formParams(getState()))) {
        dispatch(
          Auth.actions.updateFormParams(
            {
              ..._.reduce(
                defaultFields,
                (defaultFieldValues, field) => {
                  defaultFieldValues[field.key] = field.default;
                  return defaultFieldValues;
                },
                {}
              ),
            },
            id
          )
        );
        if (!preloadItems.isOauth) {
          dispatch(
            Auth.actions.updateEntityData(
              {
                selected_api: preload && preload.service && preload.service.key,
              },
              NEW_AUTH_FORM
            )
          );
        }
      }
    };
  },

  updateAuthForm(formData) {
    return Auth.actions.updateFormParams(formData, NEW_AUTH_FORM);
  },

  removeTest() {
    return (dispatch, getState) => {
      const id = Auth.selectors.id(Auth.selectors.all.authEntity(getState()));
      if (
        id &&
        id !== NEW_AUTH_FORM &&
        !Auth.selectors.all.anyAreSaving(getState())
      ) {
        dispatch(Auth.actions.deleteEntity(null, id)).catch(() => {
          // Otherwise, we'll be stuck.
          dispatch(Auth.actions.resetEntityStatus(id));
        });
      } else {
        dispatch(Auth.actions.resetEntityStatus(id));
      }
    };
  },
};

export default AuthActions;
